import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Globe, Search, Plus, MoreHorizontal, Eye, Copy,
  Trash2, Download, Play, Pause, Pencil, Upload
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { sitesAPI, exportAPI } from '@/lib/api';
import { formatDate, statusBadgeClass, statusLabel } from '@/lib/utils';
import { toast } from 'sonner';

export default function SiteManagerPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await sitesAPI.list(params);
      setSites(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${name} sitesini silmek istediginize emin misiniz?`)) return;
    try {
      await sitesAPI.delete(id);
      toast.success('Site silindi');
      loadSites();
    } catch (err) {
      toast.error('Silme basarisiz');
    }
  };

  const handleClone = async (id) => {
    try {
      await sitesAPI.clone(id);
      toast.success('Site kopyalandi');
      loadSites();
    } catch (err) {
      toast.error('Kopyalama basarisiz');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await sitesAPI.updateStatus(id, newStatus);
      toast.success(`Durum guncellendi: ${statusLabel(newStatus)}`);
      loadSites();
    } catch (err) {
      toast.error('Durum guncellenemedi');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Site Yoneticisi</h1>
          <p className="text-sm text-muted-foreground mt-1">{sites.length} site</p>
        </div>
        <div className="flex gap-3">
          <Link to="/sites/bulk">
            <Button variant="outline" className="gap-2 border-border/70">
              <Upload className="w-4 h-4" /> Toplu Yukle
            </Button>
          </Link>
          <Link to="/sites/new">
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 press-active">
              <Plus className="w-4 h-4" /> Yeni Site Olustur
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.05 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Site ara (ad veya domain)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-secondary/40 border-border/70"
            data-testid="site-manager-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="site-manager-status-filter-select">
          <SelectTrigger className="w-[180px] bg-secondary/40 border-border/70">
            <SelectValue placeholder="Durum filtresi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Durumlar</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="draft">Taslak</SelectItem>
            <SelectItem value="paused">Duraklatildi</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <Card className="border-border/70 overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-2 p-4">
                {[1,2,3].map(i => <div key={i} className="h-14 rounded-lg shimmer" />)}
              </div>
            ) : sites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Globe className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground mb-2">Henuz site yok</p>
                <Link to="/sites/new">
                  <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] mt-2">
                    <Plus className="w-4 h-4 mr-2" /> Ilk Siteni Olustur
                  </Button>
                </Link>
              </div>
            ) : (
              <Table data-testid="site-manager-sites-table">
                <TableHeader>
                  <TableRow className="bg-background/70 backdrop-blur hover:bg-background/70">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Marka</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Domain</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Durum</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Sayfalar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Son Guncelleme</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map((site) => (
                    <TableRow key={site.id} className="hover:bg-secondary/30 transition-colors duration-150">
                      <TableCell>
                        <Link to={`/sites/${site.id}/detail`} className="flex items-center gap-3 group">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: site.brand_colors?.primary || '#1a237e', color: site.brand_colors?.accent || '#ffd700' }}>
                            {site.brand_name?.charAt(0)}
                          </div>
                          <span className="font-medium group-hover:text-[hsl(var(--primary))] transition-colors duration-150">{site.brand_name}</span>
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{site.domain}</TableCell>
                      <TableCell>
                        <span className={`text-[11px] px-2.5 py-1 rounded-full ${statusBadgeClass(site.status)}`}>
                          {statusLabel(site.status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{site.pages_count || 0}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(site.updated_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="site-manager-row-actions-button">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem asChild>
                              <Link to={`/sites/${site.id}/detail`} className="gap-2">
                                <Pencil className="w-4 h-4" /> Duzenle
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/preview/${site.id}`} className="gap-2">
                                <Eye className="w-4 h-4" /> Onizle
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleClone(site.id)} className="gap-2">
                              <Copy className="w-4 h-4" /> Kopyala
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={exportAPI.download(site.id)} className="gap-2">
                                <Download className="w-4 h-4" /> Disa Aktar
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {site.status !== 'active' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(site.id, 'active')} className="gap-2 text-emerald-400">
                                <Play className="w-4 h-4" /> Yayinla
                              </DropdownMenuItem>
                            )}
                            {site.status === 'active' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(site.id, 'paused')} className="gap-2 text-amber-400">
                                <Pause className="w-4 h-4" /> Duraklat
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(site.id, site.brand_name)} className="gap-2 text-red-400">
                              <Trash2 className="w-4 h-4" /> Sil
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
